/* 三國逗陣打 · 連線的緩衝內插（其他單位的平滑移動）
 * 快照每 1/30 秒一份（v0.5.4.1，原本 1/15），但網路會抖：有時晚到、有時兩份一起到。
 * 作法：記下每個單位最近的 [伺服器時間, x, y]；畫面比伺服器時間晚一點播放（網路越抖延後越多），在前後兩份快照之間線性內插。
 * 伺服器時鐘：每份快照帶著伺服器的遊戲時間 t；用「最快到達的那幾份」估計 本地時間 → 伺服器時間 的偏移。 */

export const INTERVAL = 1 / 30; // 秒：伺服器送快照的間隔
export const DELAY = 0.06; // 秒：網路很穩時的最小延後
const WINDOW = 5; // 秒：遲到量看最近這麼久的最大值（Wi-Fi 每隔幾秒抖一次，緩衝不能抖完就馬上縮回去）
const KEEP = 1.2; // 秒：歷史保留多久

export class Clock {
  off: number | null = null;
  late = 0; // 最近 WINDOW 秒內的最大遲到量（比最快到的那份晚多少秒）
  private hist: [number, number][] = []; // [本地時間, 遲到量]
  // serverT：快照裡的伺服器時間（秒）；localT：收到時的本地時間（秒）
  observe(serverT: number, localT: number): void {
    const v = serverT - localT;
    if (this.off === null || v > this.off) this.off = v; // 到得比平常快：網路變好了，直接採用
    else this.off += (v - this.off) * 0.01; // 一直到得慢：網路變慢了，慢慢往下修
    // 以前每份快照乘 0.985 衰減，約 3 秒減半：Wi-Fi 每隔幾秒的尖峰一來緩衝就不夠，畫面停一下（v0.5.4.1 改成看最近 5 秒的最大值）
    const h = this.hist;
    h.push([localT, this.off - v]);
    while (h.length && h[0][0] < localT - WINDOW) h.shift();
    let m = 0; for (const e of h) if (e[1] > m) m = e[1];
    this.late = m;
  }
  // 延後多少：快照間隔＋最近的最大遲到量，抖得越兇緩衝越多（上限 0.3 秒）
  delay(): number { return Math.min(0.3, Math.max(DELAY, INTERVAL + this.late + 0.015)); }
  // 現在要播放的伺服器時間
  renderTime(localT: number): number { return (this.off === null ? 0 : localT + this.off) - this.delay(); }
}

export class Track {
  h: [number, number, number][] = [];
  push(t: number, x: number, y: number): void {
    const h = this.h;
    if (h.length && t <= h[h.length - 1][0]) { h[h.length - 1] = [t, x, y]; return; } // 同一時間或亂序：以新的為準
    if (h.length && t - h[h.length - 1][0] > 0.5) { this.reset(t, x, y); return; } // 離開視野一陣子又出現：不要從舊位置滑過來
    h.push([t, x, y]);
    while (h.length > 2 && h[0][0] < t - KEEP) h.shift();
  }
  reset(t: number, x: number, y: number): void { this.h = [[t, x, y]]; }
  // 在 rt 時的位置；比最舊的還早就用最舊的、比最新的還晚就停在最新的（不外推，免得轉彎時衝過頭）
  at(rt: number): [number, number] | null {
    const h = this.h;
    if (!h.length) return null;
    if (rt <= h[0][0]) return [h[0][1], h[0][2]];
    for (let i = h.length - 1; i > 0; i--) {
      const a = h[i - 1], b = h[i];
      if (rt >= a[0]) {
        if (rt >= b[0]) return [b[1], b[2]];
        const k = (rt - a[0]) / (b[0] - a[0]);
        return [a[1] + (b[1] - a[1]) * k, a[2] + (b[2] - a[2]) * k];
      }
    }
    return [h[0][1], h[0][2]];
  }
}
